import React, { useContext, useState } from "react";
import CurrentUserContext from "../../contexts/CurrentUserContext.jsx";
import { getCachedScript } from "../../utils/scriptCache.js";
import { loadLocalModule } from "../../utils/nodeLoader.js";

function ImportBeta({
  selectedTag,
  buttonTag,
  generateButtonTags,
}) {
  const NO_TEXT_LAYERS_ERROR = "No text layers were imported";
  const NO_MASKS_ERROR = "No masks were added to the project";
  const NO_SHAPES_ERROR = "No shape layers were imported";
  const NO_TRACK_MATTE_ERROR = "No track mattes were added to the project";
  const SCRIPTING_UNAVAILABLE_ERROR = "Premiere Pro scripting is not available";
  const NODE_UNAVAILABLE_ERROR = "Node.js environment not detected";
  const [isImporting, setIsImporting] = useState(false);
  const currentUser = useContext(CurrentUserContext);

  const handleClick = (script, argument) => {
    const code = getCachedScript();
    if (code) {
      window.jsx.evalScript(
        `run(${script}(${argument}));`,
        function (result) {},
      );
    }
  };

  const getNodeContext = () => {
    const nodeRequire = window.require || window.parent?.require;

    if (!nodeRequire) {
      throw new Error("Node.js environment not detected");
    }

    return {
      fs: nodeRequire("fs"),
      path: nodeRequire("path"),
    };
  };

  const buildProjectCopyPath = (projectPath) => {
    const { fs, path } = getNodeContext();
    const parsedPath = path.parse(projectPath);
    const timestamp = Date.now();
    let copyPath = path.join(
      parsedPath.dir,
      `${parsedPath.name}.__ae2_refresh__.${timestamp}${parsedPath.ext}`,
    );
    let suffix = 1;

    while (fs.existsSync(copyPath)) {
      copyPath = path.join(
        parsedPath.dir,
        `${parsedPath.name}.__ae2_refresh__.${timestamp}.${suffix}${parsedPath.ext}`,
      );
      suffix += 1;
    }

    return copyPath;
  };

  const createProjectCopy = (projectPath, copyPath) => {
    const { fs } = getNodeContext();
    fs.copyFileSync(projectPath, copyPath);
  };

  const deleteProjectCopy = (copyPath) => {
    const { fs } = getNodeContext();

    if (copyPath && fs.existsSync(copyPath)) {
      fs.unlinkSync(copyPath);
    }
  };

  const getProjectPath = () =>
    new Promise((resolve, reject) => {
      const code = getCachedScript();

      if (!code || !window?.jsx?.evalScript) {
        reject(new Error("Premiere Pro scripting is not available"));
        return;
      }

      const script = `
        try {
          if (app && app.project && app.project.path) {
            app.project.path;
          } else {
            "__AE2_EMPTY_PROJECT_PATH__";
          }
        } catch (error) {
          "__AE2_PROJECT_PATH_ERROR__" + error.toString();
        }
      `;

      window.jsx.evalScript(script, (result) => {
        if (!result || result === "__AE2_EMPTY_PROJECT_PATH__") {
          reject(new Error("Save the Premiere Pro project before import"));
          return;
        }

        if (result.indexOf("__AE2_PROJECT_PATH_ERROR__") === 0) {
          reject(
            new Error(result.replace("__AE2_PROJECT_PATH_ERROR__", "").trim()),
          );
          return;
        }

        resolve(result);
      });
    });

  const saveProject = () =>
    new Promise((resolve, reject) => {
      const code = getCachedScript();

      if (!code || !window?.jsx?.evalScript) {
        reject(new Error("Premiere Pro scripting is not available"));
        return;
      }

      const script = `
        try {
          app.project.save();
        } catch (error) {
          "__AE2_SAVE_ERROR__" + error.toString();
        }
      `;

      window.jsx.evalScript(script, (result) => {
        if (
          result === "true" ||
          result === "0" ||
          result === true ||
          result === 0
        ) {
          resolve();
          return;
        }

        reject(
          new Error(
            (result || "__AE2_SAVE_ERROR__Project save failed")
              .replace("__AE2_SAVE_ERROR__", "")
              .trim(),
          ),
        );
      });
    });

  const refreshProjectThroughCopy = (projectPath, copyPath) =>
    new Promise((resolve, reject) => {
      const code = getCachedScript();

      if (!code || !window?.jsx?.evalScript) {
        reject(new Error("Premiere Pro scripting is not available"));
        return;
      }

      const script = `
        try {
          var originalPath = ${JSON.stringify(projectPath)};
          var copyPath = ${JSON.stringify(copyPath)};
          var openCopyResult = app.openDocument(copyPath, 1, 1, 1, 1);

          if (openCopyResult !== true) {
            "__AE2_REFRESH_ERROR__openCopy:" + openCopyResult;
          } else {
            var projectsCountAfterCopy = app.projects.numProjects || app.projects.length || 0;
            var closeOriginalResult = "__not_found__";

            for (var j = 0; j < projectsCountAfterCopy; j += 1) {
              var originalProjectItem = app.projects[j];
              if (originalProjectItem && originalProjectItem.path === originalPath) {
                closeOriginalResult = originalProjectItem.closeDocument(0, 0);
                break;
              }
            }

            if (closeOriginalResult !== 0 && closeOriginalResult !== true) {
              "__AE2_REFRESH_ERROR__closeOriginal:" + closeOriginalResult;
            } else {
              var openOriginalResult = app.openDocument(originalPath, 1, 1, 1, 1);

              if (openOriginalResult !== true) {
                "__AE2_REFRESH_ERROR__openOriginal:" + openOriginalResult;
              } else {
                var closeCopyResult = "__not_found__";
                var projectsCount = app.projects.numProjects || app.projects.length || 0;

                for (var i = 0; i < projectsCount; i += 1) {
                  var projectItem = app.projects[i];
                  if (projectItem && projectItem.path === copyPath) {
                    closeCopyResult = projectItem.closeDocument(0, 0);
                    break;
                  }
                }

                if (closeCopyResult !== 0 && closeCopyResult !== true) {
                  "__AE2_REFRESH_ERROR__closeCopy:" + closeCopyResult;
                } else if (app.project.path !== originalPath) {
                  "__AE2_REFRESH_ERROR__activeProject:" + app.project.path;
                } else {
                  "ok";
                }
              }
            }
          }
        } catch (error) {
          "__AE2_REFRESH_ERROR__" + error.toString();
        }
      `;

      window.jsx.evalScript(script, (result) => {
        if (result === "ok") {
          resolve();
          return;
        }

        reject(
          new Error(
            (result || "__AE2_REFRESH_ERROR__Project refresh failed")
              .replace("__AE2_REFRESH_ERROR__", "")
              .trim(),
          ),
        );
      });
    });

  const runLocalImportModule = (moduleName, methodName, projectPath) =>
    new Promise((resolve, reject) => {
      const module = loadLocalModule(moduleName);

      if (!module || typeof module[methodName] !== "function") {
        reject(new Error(`Failed to load ${moduleName} module`));
        return;
      }

      let settled = false;

      const finalize = (result) => {
        if (settled) {
          return;
        }

        settled = true;

        if (result?.error) {
          reject(new Error(result.error));
          return;
        }

        resolve(result || null);
      };

      try {
        const result = module[methodName](projectPath, finalize);

        if (typeof result !== "undefined") {
          finalize(result);
        }
      } catch (error) {
        reject(error);
      }
    });

  const getErrorMessage = (error) => {
    const message =
      typeof error?.message === "string" ? error.message.trim() : "";
    return message || "Import failed";
  };

  const shouldAlertError = (error) => {
    const message = getErrorMessage(error);

    return (
      message !== NO_TEXT_LAYERS_ERROR &&
      message !== NO_MASKS_ERROR &&
      message !== NO_SHAPES_ERROR &&
      message !== NO_TRACK_MATTE_ERROR &&
      message !== SCRIPTING_UNAVAILABLE_ERROR &&
      message !== NODE_UNAVAILABLE_ERROR
    );
  };

  const handleImport = async () => {
    if (isImporting) {
      return;
    }

    let projectCopyPath = null;

    try {
      setIsImporting(true);

      const projectPath = await getProjectPath();
      await saveProject();
      projectCopyPath = buildProjectCopyPath(projectPath);

      // try {
      //   await runLocalImportModule(
      //     "EnabledZeroTrack",
      //     "enabledZeroTrack",
      //     projectPath,
      //   );
      // } catch (error) {
      //   throw error;
      // }

      try {
        await runLocalImportModule(
          "importTextLayers",
          "importTextLayers",
          projectPath,
        );
      } catch (error) {
        if ((error.message || "").trim() !== NO_TEXT_LAYERS_ERROR) {
          throw error;
        }
      }

      // Shape layers the export rendered (modifiers with no vector export) become paths in shapeLayers.json.
      await runLocalImportModule(
        "traceShapeLayers",
        "traceTempShapeLayers",
        projectPath,
      );

      try {
        await runLocalImportModule(
          "importShapeLayers",
          "importShapeLayers",
          projectPath,
        );
      } catch (error) {
        if ((error.message || "").trim() !== NO_SHAPES_ERROR) {
          throw error;
        }
      }

      try {
        await runLocalImportModule(
          "importMaskPath",
          "importMaskPath",
          projectPath,
        );
      } catch (error) {
        if ((error.message || "").trim() !== NO_MASKS_ERROR) {
          throw error;
        }
      }

      try {
        await runLocalImportModule("importColors", "importColors", projectPath);
      } catch (error) {
        throw error;
      }

      try {
        await runLocalImportModule(
          "importTrackMatte",
          "importTrackMatte",
          projectPath,
        );
      } catch (error) {
        if ((error.message || "").trim() !== NO_TRACK_MATTE_ERROR) {
          throw error;
        }
      }

      try {
        await runLocalImportModule(
          "restoreTrackItemName",
          "restoreTrackItemName",
          projectPath,
        );
      } catch (error) {
        throw error;
      }

      try {
        await runLocalImportModule(
          "importProjectStructure",
          "importProjectStructure",
          projectPath,
        );
      } catch (error) {
        // если есть свой кастомный NO_ERROR — добавь сюда проверку
        // иначе просто пробрасываем
        throw error;
      }

      try {
        await runLocalImportModule(
          "ImportLockLayer",
          "importLockLayer",
          projectPath,
        );
      } catch (error) {
        throw error;
      }

      try {
        await runLocalImportModule(
          "normalizeAudioTrackItem",
          "normalizeAudioTrackItem",
          projectPath,
        );
      } catch (error) {
        throw error;
      }
      
      createProjectCopy(projectPath, projectCopyPath);
      await refreshProjectThroughCopy(projectPath, projectCopyPath);
      deleteProjectCopy(projectCopyPath);

      handleClick(
        "importAEData",
        JSON.stringify({
          name: currentUser.login,
          trackMatte: true,
          setMatte: false,
          legacy: false,
        }),
      );
    } catch (error) {
      try {
        deleteProjectCopy(projectCopyPath);
      } catch (cleanupError) {}
      if (shouldAlertError(error)) {
        alert(getErrorMessage(error));
      }
    } finally {
      setIsImporting(false);
    }
  };

  if (selectedTag && selectedTag !== buttonTag) {
    return null;
  }

  return (
    <li
      onClick={handleImport}
      className={`button-card ${isImporting ? "active" : ""}`}
    >
      <div className="button-card__icon">
        <i class="fa-sharp fa-regular fa-arrow-up-to-arc fa-flip-vertical"></i>
        <div />
      </div>
      <div className="button-card__title">
        {isImporting ? "Importing..." : "Import Beta"}
      </div>
      {generateButtonTags()}
    </li>
  );
}

export default ImportBeta;
